import React, { useState } from "react";
import { Button, styled, Box } from "@mui/material";
import type { ButtonProps } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../theme/tokens";

type TrackNowButtonVariant = "primary" | "secondary" | "tertiary" | "destructive" | "compact";

interface TrackNowButtonProps {
  variant?: TrackNowButtonVariant;
  children?: React.ReactNode;
  disabled?: boolean;
  onClick?: (event?: any) => void;
}

interface StyledButtonProps extends ButtonProps {
  buttonvariant: TrackNowButtonVariant;
  ispressed: boolean;
}

const CustomArrowIcon = ({ hover, disabled, variant }: { hover: boolean; disabled?: boolean; variant: TrackNowButtonVariant }) => {
  const theme = useTheme();
  const primaryMain = theme.palette.primary.main;

  const getRotation = () => {
    if (variant === "primary" || variant === "destructive") {
      return hover && !disabled ? "rotate(-45deg)" : "rotate(0deg)";
    }
    return "rotate(0deg)"; // Secondary and tertiary don't rotate
  };

  const showCircle = variant === "primary" || variant === "destructive";

  if (showCircle) {
    const arrowColor =
      variant === "destructive" ? "#dc2626" : primaryMain;
    return (
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: "50%",
          backgroundColor: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "transform 0.2s ease",
          transform: getRotation(),
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          style={{ color: arrowColor }}
        >
          <path
            d="M2 6L10 6M10 6L7 3M10 6L7 9"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Box>
    );
  }

  // No circle for secondary and tertiary
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 12 12"
      fill="none"
      style={{
        color: variant === "secondary" ? "white" : primaryMain,
        transition: "opacity 0.2s ease",
        opacity: disabled ? 0.5 : 1
      }}
    >
      <path
        d="M2 6L10 6M10 6L7 3M10 6L7 9"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const BaseButton = styled(Button)<StyledButtonProps>(({ theme, buttonvariant, ispressed }) => {
  const primaryMain = theme.palette.primary.main;

  const baseStyles = {
    borderRadius: tokens.radius.pill,
    height: 48,
    fontSize: 16,
    fontWeight: 700,
    letterSpacing: "0.3px",
    textTransform: "none" as const,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "none",
    border: "none",
    cursor: "pointer",
    transition: "all 0.2s ease",
    fontFamily: theme.typography.fontFamily || "Area Normal, Inter, sans-serif",

    "&:focus-visible": {
      outline: `2px dashed ${primaryMain}`,
      outlineOffset: 4,
    },

    "&:disabled": {
      backgroundColor: `${tokens.colors.grey} !important`,
      color: "white !important",
      cursor: "not-allowed",
      border: "none !important",
    },
  };

  const variantStyles = {
    primary: {
      ...baseStyles,
      paddingLeft: 20,
      paddingRight: 16,
      gap: 12,
      minWidth: "fit-content",
      whiteSpace: "nowrap" as const,
      margin: "0 4px",
      borderRadius: "24px",
      backgroundColor: primaryMain,
      color: tokens.colors.white,
      "&:hover:not(:disabled)": {
        backgroundColor: primaryMain,
        paddingRight: 20,
      },
      "&:active": {
        backgroundColor: `color-mix(in srgb, ${primaryMain} 80%, black)`,
      },
    },
    secondary: {
      ...baseStyles,
      paddingLeft: 20,
      paddingRight: 16,
      gap: 12,
      minWidth: "fit-content",
      whiteSpace: "nowrap" as const,
      margin: "0 4px",
      borderRadius: "24px",
      backgroundColor: primaryMain,
      color: tokens.colors.white,
      "&:hover:not(:disabled)": {
        backgroundColor: primaryMain,
        paddingRight: 20,
      },
      "&:active": {
        backgroundColor: `color-mix(in srgb, ${primaryMain} 80%, black)`,
      },
    },
    tertiary: {
      ...baseStyles,
      paddingLeft: 20,
      paddingRight: 16,
      gap: 12,
      minWidth: "fit-content",
      whiteSpace: "nowrap" as const,
      margin: "0 4px",
      backgroundColor: "transparent",
      color: primaryMain,
      border: `2px solid ${primaryMain}`,
      "&:hover:not(:disabled)": {
        backgroundColor: "transparent",
        paddingRight: 20,
      },
      "&:active": {
        backgroundColor: `color-mix(in srgb, ${primaryMain} 12%, transparent)`,
      },
      "&:disabled": {
        ...baseStyles["&:disabled"],
        backgroundColor: "transparent !important",
        color: `${tokens.colors.grey} !important`,
        border: `2px solid ${tokens.colors.grey} !important`,
      },
    },
    destructive: {
      ...baseStyles,
      paddingLeft: 20,
      paddingRight: 16,
      gap: 12,
      minWidth: "fit-content",
      whiteSpace: "nowrap" as const,
      margin: "0 4px",
      backgroundColor: "#dc2626",
      color: tokens.colors.white,
      "&:hover:not(:disabled)": {
        backgroundColor: "#dc2626",
        paddingRight: 20,
      },
      "&:active": {
        backgroundColor: "#991b1b",
      },
    },
    compact: {
      ...baseStyles,
      padding: "4px 8px",
      gap: 4,
      minWidth: "fit-content",
      whiteSpace: "nowrap" as const,
      margin: 0,
      backgroundColor: "transparent",
      color: primaryMain,
      height: "auto",
      fontSize: 14,
      fontWeight: 400,
      "&:hover:not(:disabled)": {
        backgroundColor: "transparent",
      },
      "&:active": {
        backgroundColor: "transparent",
      },
      "&:disabled": {
        ...baseStyles["&:disabled"],
        backgroundColor: "transparent !important",
        color: `${tokens.colors.grey} !important`,
      },
    },
  };

  return variantStyles[buttonvariant];
});

export const TrackNowButton = ({
  variant = "primary",
  children,
  disabled,
  onClick,
}: TrackNowButtonProps) => {
  const [hover, setHover] = useState(false);
  const [pressed, setPressed] = useState(false);

  const showArrow = () => {
    if (variant === "primary" || variant === "destructive") {
      return true; // Always show, rotates on hover
    }
    if (variant === "secondary" || variant === "tertiary") {
      return hover && !disabled; // Show only on hover, no rotation
    }
    if (variant === "compact" && hover && !disabled) {
      return true;
    }
    return false;
  };

  return (
    <BaseButton
      buttonvariant={variant}
      ispressed={pressed}
      disableRipple
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => {
        setHover(false);
        setPressed(false);
      }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
    >
      {children}
      {showArrow() && <CustomArrowIcon hover={hover} disabled={disabled || false} variant={variant} />}
    </BaseButton>
  );
};
